/**
 * /control/requerimientos · M-15.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../../lib/api";
import { REQ_ESTADOS, AUTORIDAD_LABEL, fmtFecha, diasHasta, SEMAFORO } from "./_common";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { Gavel, Plus, FileDown, UserCog, Send, Archive, AlertTriangle } from "lucide-react";

export function RequerimientosList() {
    const [items, setItems] = useState([]);
    const [filtro, setFiltro] = useState("todos");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        autoridad: "sat", tipo: "informacion",
        nombre_oficio: "", numero_oficio: "",
        plazo: 15, medio: "email_institucional", conducto: "via_cnbv",
        contenido: "", sensibilidad: "normal",
    });
    const nav = useNavigate();

    const load = () => {
        const p = filtro === "todos" ? {} : { estado: filtro };
        api.get("/control/requerimientos", { params: p }).then(r => setItems(r.data));
    };
    useEffect(() => { load(); }, [filtro]);

    const crear = async () => {
        if (!form.nombre_oficio || !form.numero_oficio || form.contenido.length < 10) {
            toast.error("Nombre/número oficio y contenido (≥10) requeridos"); return;
        }
        try {
            const r = await api.post("/control/requerimientos", {
                autoridad_solicitante: form.autoridad,
                tipo: form.tipo,
                nombre_oficio: form.nombre_oficio,
                numero_oficio_autoridad: form.numero_oficio,
                fecha_recepcion: new Date().toISOString(),
                medio_recepcion: form.medio,
                plazo_dias_naturales: Number(form.plazo),
                conducto: form.conducto,
                contenido_resumen: form.contenido,
                sensibilidad: form.sensibilidad,
            });
            toast.success(`Requerimiento registrado · ${r.data.folio_requerimiento}`);
            setOpen(false);
            nav(`/control/requerimientos/${r.data.id}`);
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };

    return (
        <div className="space-y-6" data-testid="requerimientos-list">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-3xl font-serif font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
                        Requerimientos de Autoridad · M-15
                    </h1>
                    <p className="text-sm text-muted-foreground">Ventanilla única · Secreto bursátil LMV 192.</p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white"
                            data-testid="btn-nuevo-req">
                            <Plus className="h-4 w-4 mr-1" /> Nuevo requerimiento
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader><DialogTitle>Registrar requerimiento</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-semibold">Autoridad</label>
                                    <Select value={form.autoridad} onValueChange={v => setForm({ ...form, autoridad: v })}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(AUTORIDAD_LABEL).map(([k, v]) =>
                                                <SelectItem key={k} value={k}>{v}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold">Tipo</label>
                                    <Select value={form.tipo} onValueChange={v => setForm({ ...form, tipo: v })}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {["informacion", "embargo", "aseguramiento", "bloqueo", "otro"].map(t =>
                                                <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-semibold">Nombre del oficio</label>
                                <Input value={form.nombre_oficio}
                                    onChange={e => setForm({ ...form, nombre_oficio: e.target.value })} />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-semibold">Número oficio autoridad</label>
                                    <Input value={form.numero_oficio}
                                        onChange={e => setForm({ ...form, numero_oficio: e.target.value })} />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold">Plazo (días naturales)</label>
                                    <Input type="number" min="1" max="180" value={form.plazo}
                                        onChange={e => setForm({ ...form, plazo: e.target.value })} />
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className="text-xs font-semibold">Medio</label>
                                    <Select value={form.medio} onValueChange={v => setForm({ ...form, medio: v })}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {["ventanilla_fisica", "email_institucional", "notificacion_electronica", "oficio_fisico"].map(m =>
                                                <SelectItem key={m} value={m}>{m}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold">Conducto</label>
                                    <Select value={form.conducto} onValueChange={v => setForm({ ...form, conducto: v })}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="directo">Directo</SelectItem>
                                            <SelectItem value="via_cnbv">Vía CNBV</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold">Sensibilidad</label>
                                    <Select value={form.sensibilidad} onValueChange={v => setForm({ ...form, sensibilidad: v })}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="normal">Normal</SelectItem>
                                            <SelectItem value="alta">Alta</SelectItem>
                                            <SelectItem value="critica">Crítica</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-semibold">Contenido del requerimiento (≥10)</label>
                                <Textarea rows={3} value={form.contenido}
                                    onChange={e => setForm({ ...form, contenido: e.target.value })} />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                            <Button onClick={crear} data-testid="btn-crear-req">Registrar</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="flex gap-2 items-center flex-wrap">
                <span className="text-xs text-muted-foreground">Estado:</span>
                {["todos", "recibido", "en_analisis", "en_respuesta", "respondido", "archivado"].map(e =>
                    <Button key={e} size="sm" variant={filtro === e ? "default" : "outline"}
                        onClick={() => setFiltro(e)}>{e}</Button>)}
            </div>
            <Card>
                <CardContent className="p-0">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/40 text-xs uppercase">
                            <tr>
                                <th className="px-3 py-2 text-left">Folio</th>
                                <th className="px-3 py-2 text-left">Autoridad</th>
                                <th className="px-3 py-2 text-left">Tipo</th>
                                <th className="px-3 py-2 text-right">Vence</th>
                                <th className="px-3 py-2 text-right">Días</th>
                                <th className="px-3 py-2 text-left">Sens.</th>
                                <th className="px-3 py-2 text-left">Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && (
                                <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">
                                    Sin requerimientos.
                                </td></tr>
                            )}
                            {items.map(r => {
                                const cfg = REQ_ESTADOS[r.estado] || { label: r.estado, cls: "" };
                                const dias = diasHasta(r.fecha_limite_respuesta);
                                const sem = dias === null ? "gris" : dias < 0 || dias <= 3 ? "rojo"
                                    : dias <= 15 ? "ambar" : "verde";
                                return (
                                    <tr key={r.id} className="border-t hover:bg-muted/20">
                                        <td className="px-3 py-2 font-mono text-xs">
                                            <Link to={`/control/requerimientos/${r.id}`}
                                                className="text-primary hover:underline">
                                                {r.folio_requerimiento}
                                            </Link>
                                        </td>
                                        <td className="px-3 py-2 text-xs">{AUTORIDAD_LABEL[r.autoridad_solicitante]}</td>
                                        <td className="px-3 py-2 text-xs">{r.tipo}</td>
                                        <td className="px-3 py-2 text-right text-xs">{fmtFecha(r.fecha_limite_respuesta)}</td>
                                        <td className="px-3 py-2 text-right">
                                            {dias !== null && (
                                                <Badge className={SEMAFORO[sem] + " border text-xs"}>
                                                    {dias}
                                                </Badge>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-xs uppercase">{r.sensibilidad}</td>
                                        <td className="px-3 py-2"><Badge className={cfg.cls}>{cfg.label}</Badge></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </CardContent>
            </Card>
        </div>
    );
}


export function RequerimientoDetalle() {
    const { id } = useParams();
    const [r, setR] = useState(null);
    const reload = () => api.get(`/control/requerimientos/${id}`).then(x => setR(x.data));
    useEffect(() => { reload(); }, [id]);
    if (!r) return <div className="p-6 text-muted-foreground">Cargando…</div>;

    const cfg = REQ_ESTADOS[r.estado] || { label: r.estado, cls: "" };
    const dias = diasHasta(r.fecha_limite_respuesta);

    const asignar = async () => {
        try {
            await api.post(`/control/requerimientos/${id}/asignar`, {
                juridico_responsable_email: "juridico@trafalgar.mx",
                cumplimiento_responsable_email: "cumplimiento@trafalgar.mx",
            });
            toast.success("Asignado a Jurídico + Cumplimiento");
            reload();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };
    const responder = async () => {
        const resumen = prompt("Resumen de la respuesta (≥10 caracteres):");
        if (!resumen || resumen.length < 10) return;
        try {
            await api.post(`/control/requerimientos/${id}/responder`, {
                medio_envio: "oficio_fisico", resumen_respuesta: resumen,
            });
            toast.success("Respuesta registrada");
            reload();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };
    const archivar = async () => {
        try {
            await api.post(`/control/requerimientos/${id}/archivar`);
            toast.success("Archivado");
            reload();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };
    const pdf = () => window.open(`${api.defaults.baseURL}/control/requerimientos/${id}/pdf`, "_blank");

    const timeline = [
        { key: "recibido", label: "Recibido", date: r.created_at, done: true },
        { key: "en_analisis", label: "En análisis", date: null,
          done: ["en_analisis", "en_respuesta", "respondido", "archivado"].includes(r.estado) },
        { key: "respondido", label: "Respondido", date: r.respuesta?.fecha_envio,
          done: ["respondido", "archivado"].includes(r.estado) },
        { key: "archivado", label: "Archivado", date: null,
          done: r.estado === "archivado" },
    ];

    return (
        <div className="space-y-6" data-testid="requerimiento-detalle">
            <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-serif font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
                    {r.folio_requerimiento}
                </h1>
                <Badge className={cfg.cls}>{cfg.label}</Badge>
                <Badge className="bg-slate-100 text-slate-800 border-slate-200 border uppercase">
                    {r.sensibilidad}
                </Badge>
                {dias !== null && dias <= 3 && (
                    <Badge className="bg-red-100 text-red-900 border-red-300 border">
                        <AlertTriangle className="h-3 w-3 inline mr-1" />
                        {dias < 0 ? "Vencido" : `${dias} días restantes`}
                    </Badge>
                )}
            </div>

            {/* Timeline visual */}
            <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">Timeline</CardTitle></CardHeader>
                <CardContent>
                    <div className="flex items-center gap-2" data-testid="timeline-req">
                        {timeline.map((step, i) => (
                            <div key={step.key} className="flex items-center gap-2 flex-1">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold
                                    ${step.done ? "bg-emerald-600 text-white" : "bg-muted text-muted-foreground"}`}>
                                    {i + 1}
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs font-semibold">{step.label}</p>
                                    <p className="text-xs text-muted-foreground">{fmtFecha(step.date)}</p>
                                </div>
                                {i < timeline.length - 1 && (
                                    <div className={`h-0.5 flex-1 ${timeline[i + 1].done ? "bg-emerald-600" : "bg-muted"}`} />
                                )}
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="md:col-span-2">
                    <CardHeader><CardTitle className="text-base">Oficio recibido</CardTitle></CardHeader>
                    <CardContent className="text-sm space-y-2">
                        <p><b>Autoridad:</b> {AUTORIDAD_LABEL[r.autoridad_solicitante]}</p>
                        <p><b>Nombre:</b> {r.nombre_oficio}</p>
                        <p><b>Número:</b> {r.numero_oficio_autoridad}</p>
                        <p><b>Fecha recepción:</b> {fmtFecha(r.fecha_recepcion)} · <b>Plazo:</b> {r.plazo_dias_naturales} días</p>
                        <p><b>Vence:</b> {fmtFecha(r.fecha_limite_respuesta)}</p>
                        <p><b>Conducto:</b> {r.conducto === "via_cnbv" ? "Vía CNBV" : "Directo"}</p>
                        <p className="text-xs text-muted-foreground mt-2">{r.contenido_resumen}</p>
                        {r.asignado_a && (
                            <div className="mt-3 text-xs">
                                <b>Asignado:</b> Jurídico: {r.asignado_a.juridico_responsable} ·
                                Cumplimiento: {r.asignado_a.cumplimiento_responsable}
                            </div>
                        )}
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader><CardTitle className="text-base">Acciones</CardTitle></CardHeader>
                    <CardContent className="space-y-2">
                        {r.estado === "recibido" && (
                            <Button className="w-full" variant="outline" onClick={asignar}
                                data-testid="btn-asignar">
                                <UserCog className="h-4 w-4 mr-1" /> Asignar
                            </Button>
                        )}
                        {["en_analisis", "en_respuesta"].includes(r.estado) && (
                            <Button className="w-full bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white"
                                onClick={responder} data-testid="btn-responder">
                                <Send className="h-4 w-4 mr-1" /> Responder
                            </Button>
                        )}
                        {r.estado === "respondido" && (
                            <Button className="w-full" variant="outline" onClick={archivar}
                                data-testid="btn-archivar">
                                <Archive className="h-4 w-4 mr-1" /> Archivar
                            </Button>
                        )}
                        <Button className="w-full" variant="outline" onClick={pdf}>
                            <FileDown className="h-4 w-4 mr-1" /> PDF respuesta
                        </Button>
                    </CardContent>
                </Card>
            </div>

            {r.respuesta?.resumen_respuesta && (
                <Card>
                    <CardHeader><CardTitle className="text-base">Respuesta</CardTitle></CardHeader>
                    <CardContent className="text-sm">
                        <p>{r.respuesta.resumen_respuesta}</p>
                        <p className="text-xs text-muted-foreground mt-2">
                            Enviada: {fmtFecha(r.respuesta.fecha_envio)} · Medio: {r.respuesta.medio_envio} ·
                            Firmado por: {r.respuesta.firmado_por}
                        </p>
                        {r.respuesta.hash_respuesta && (
                            <p className="text-xs font-mono text-muted-foreground mt-1">
                                Hash: {r.respuesta.hash_respuesta.slice(0, 32)}…
                            </p>
                        )}
                    </CardContent>
                </Card>
            )}
        </div>
    );
}

export default RequerimientosList;
